import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Pencil, Trash2, Plus } from 'lucide-react';
import { deleteCompanyCascade } from '@/lib/companyCascadeDelete';

const UNDO_COUNTDOWN_SECONDS = 10;

export function OwnerCompaniesPage() {
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const [companiesWithOwner, setCompaniesWithOwner] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<{ id: string; name: string } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [undoCountdown, setUndoCountdown] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const cancelledRef = useRef(false);

  // Cargar empresas y verificar cuáles tienen usuarios con role 'owner' asociados
  useEffect(() => {
    const load = async () => {
      const [companiesSnap, usersSnap] = await Promise.all([
        getDocs(collection(db, 'companies')),
        getDocs(collection(db, 'users')),
      ]);

      const companiesList = companiesSnap.docs.map((d) => ({
        id: d.id,
        name: d.data().name ?? d.id,
      }));

      const withOwner = new Set<string>();
      usersSnap.docs.forEach((d) => {
        const data = d.data();
        if (data.role === 'owner' && data.companyId) {
          const cid = typeof data.companyId === 'string' ? data.companyId : (data.companyId as { id?: string })?.id;
          if (cid) withOwner.add(cid);
        }
      });

      setCompanies(companiesList);
      setCompaniesWithOwner(withOwner);
      setLoading(false);
    };
    load();
  }, []);

  const executeDelete = useCallback(async (company: { id: string; name: string }) => {
    const { id: companyId } = company;
    setPendingDelete(null);
    setDeleting(true);
    try {
      await deleteCompanyCascade(companyId);
      setCompanies((prev) => prev.filter((c) => c.id !== companyId));
      setCompaniesWithOwner((prev) => {
        const next = new Set(prev);
        next.delete(companyId);
        return next;
      });
    } catch (err) {
      console.error('[OwnerCompanies] Error eliminando empresa:', err);
      setCompanies((prev) => {
        const exists = prev.some((c) => c.id === companyId);
        if (!exists) return [...prev, company].sort((a, b) => a.name.localeCompare(b.name));
        return prev;
      });
    } finally {
      setDeleting(false);
    }
  }, []);

  // Countdown para el undo
  useEffect(() => {
    if (pendingDelete == null || undoCountdown <= 0) return;
    cancelledRef.current = false;
    const t = setInterval(() => {
      setUndoCountdown((c) => {
        if (c <= 1) {
          clearInterval(t);
          if (!cancelledRef.current && pendingDelete) executeDelete(pendingDelete);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => {
      clearInterval(t);
      cancelledRef.current = true;
    };
  }, [pendingDelete, executeDelete]);

  const handleDeleteClick = (company: { id: string; name: string }) => {
    setCompanyToDelete(company);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!companyToDelete) return;
    setConfirmDeleteOpen(false);
    setPendingDelete(companyToDelete);
    setUndoCountdown(UNDO_COUNTDOWN_SECONDS);
    setCompanies((prev) => prev.filter((c) => c.id !== companyToDelete.id));
    setCompanyToDelete(null);
  };

  const handleUndoRestore = useCallback(() => {
    if (!pendingDelete) return;
    cancelledRef.current = true;
    setCompanies((prev) => [...prev, pendingDelete].sort((a, b) => a.name.localeCompare(b.name)));
    setPendingDelete(null);
    setUndoCountdown(0);
  }, [pendingDelete]);

  const hasOwner = (companyId: string) => companiesWithOwner.has(companyId);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Empresas</h1>
        <Button asChild>
          <Link to="/owner/companies/new">
            <Plus className="mr-1.5 h-4 w-4" />
            Crear empresa
          </Link>
        </Button>
      </div>
      {loading ? (
        <p className="text-muted-foreground">Cargando...</p>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Listado</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="pb-2 pr-4 text-left font-medium">Nombre</th>
                    <th className="pb-2 pr-4 text-right font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((c) => (
                    <tr key={c.id} className="border-b last:border-0">
                      <td className="py-3 pr-4">
                        <Link to={`/owner/companies/${c.id}`} className="text-primary hover:underline">
                          {c.name}
                        </Link>
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/owner/companies/${c.id}`} title="Editar">
                              <Pencil className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={hasOwner(c.id)}
                            title={
                              hasOwner(c.id)
                                ? 'No se puede eliminar: la empresa tiene usuarios con rol Owner'
                                : 'Eliminar empresa'
                            }
                            onClick={() => handleDeleteClick(c)}
                            className="text-destructive hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog de confirmación */}
      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar empresa?</DialogTitle>
            <DialogDescription>
              Se eliminará la empresa <strong>{companyToDelete?.name}</strong> y TODOS sus datos
              (usuarios, procesos, resultados). Esta acción es irreversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toast de "Empresa eliminada" con Deshacer */}
      {pendingDelete && (
        <div className="fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-4 rounded-lg border bg-card px-4 py-3 shadow-lg">
          <span className="text-sm font-medium">Empresa eliminada</span>
          <Button variant="outline" size="sm" onClick={handleUndoRestore}>
            Deshacer
          </Button>
          <span className="text-sm text-muted-foreground">
            Se eliminará en {undoCountdown}s
          </span>
        </div>
      )}

      {deleting && (
        <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-lg border bg-card px-4 py-2 text-sm">
          Eliminando...
        </div>
      )}
    </div>
  );
}
